import {trans} from '@frontend/helpers'
import {normalizeNumberFromUrl} from '@frontend/helpers/normalizeNumberFromUrl'
import type {
    AdsListRouteParams,
    FilteredItemsPageUrlQueryParams,
} from '@frontend/modules/public/ads/AdsModuleTypes'
import {PriceFilter} from '@frontend/modules/public/ads/components/category_filters/filters/PriceFilter'
import {
    type MouseEvent,
    type SubmitEvent,
    useEffect,
    useState,
} from 'react'
import {useParams} from 'react-router-dom'
import {Button} from 'swayok-react-mdb-ui-kit/components/Button/Button'
import {Card} from 'swayok-react-mdb-ui-kit/components/Card/Card'
import {CardBody} from 'swayok-react-mdb-ui-kit/components/Card/CardBody'
import {CardFooter} from 'swayok-react-mdb-ui-kit/components/Card/CardFooter'
import {CardHeader} from 'swayok-react-mdb-ui-kit/components/Card/CardHeader'
import {CardTitle} from 'swayok-react-mdb-ui-kit/components/Card/CardTitle'
import {ModalBody} from 'swayok-react-mdb-ui-kit/components/Modal/ModalBody'
import {ModalFooter} from 'swayok-react-mdb-ui-kit/components/Modal/ModalFooter'
import {ModalHeader} from 'swayok-react-mdb-ui-kit/components/Modal/ModalHeader'
import {useEventCallback} from 'swayok-react-mdb-ui-kit/helpers/useEventCallback'
import {useUrlQueryParams} from 'swayok-react-mdb-ui-kit/helpers/useUrlQueryParams'
import type {AnyObject} from 'swayok-react-mdb-ui-kit/types/Common'
import {AdCategoryFiltersSubcategoriesNav} from './AdCategoryFiltersSubcategoriesNav'
import {
    AdCategoryPropertyFilters,
    type AdCategoryPropertyFiltersFormValues,
} from './AdCategoryPropertyFilters'

interface Props {
    forModal?: boolean
    services?: boolean
    onClose?: () => void
}

// Панель фильтрации списка объявлений.
export function AdCategoryFiltersCard(props: Props) {

    const {
        forModal,
        services,
        onClose,
    } = props

    // Данные о текущем URL.
    const routeParams = useParams<AdsListRouteParams>()
    const categoryId: number | null = normalizeNumberFromUrl(routeParams.id)
    const {
        urlQueryParams,
        setUrlQueryParams,
        urlQueryData,
    } = useUrlQueryParams<FilteredItemsPageUrlQueryParams>()

    // Фильтры.
    const [
        filters,
        setFilters,
    ] = useState<AdCategoryPropertyFiltersFormValues>({})
    // Предотвращение спама.
    const [
        isSubmitting,
        setIsSubmitting,
    ] = useState<boolean>(false)

    // Обновление состояний при изменении соответствующих параметров в URL query.
    useEffect(() => {
        setFilters(filters => ({
            ...filters,
            minPrice: normalizePrice(urlQueryParams.get('minPrice')),
            maxPrice: normalizePrice(urlQueryParams.get('maxPrice')),
        }))
    }, [urlQueryParams.get('minPrice'), urlQueryParams.get('maxPrice')])

    useEffect(() => {
        setFilters(filters => ({
            ...filters,
            props: decodeFilterValuesFromJson(urlQueryParams.get('props')),
        }))
    }, [urlQueryParams.get('props')])

    // Применение фильтров.
    const onSubmit = useEventCallback((
        event?: MouseEvent<HTMLElement> | SubmitEvent<HTMLElement>
    ) => {
        event?.preventDefault()
        onClose?.()
        if (isSubmitting) {
            return
        }
        // Блокировка повторных запросов на небольшое время, чтобы предотвратить спам.
        setIsSubmitting(true)
        setTimeout(() => setIsSubmitting(false), 1500)

        if (!filters.minPrice) {
            urlQueryParams.delete('minPrice')
        } else {
            urlQueryParams.set('minPrice', String(filters.minPrice))
        }

        if (!filters.maxPrice) {
            urlQueryParams.delete('maxPrice')
        } else {
            urlQueryParams.set('maxPrice', String(filters.maxPrice))
        }

        const propsValueForUrlQuery = encodeFilterValuesToJson(filters.props)
        if (propsValueForUrlQuery) {
            urlQueryParams.set('props', propsValueForUrlQuery)
        } else {
            urlQueryParams.delete('props')
        }

        setUrlQueryParams(urlQueryParams)
    })

    const submitButton = (
        <Button
            color="blue"
            type="submit"
            className="full-width"
            onClick={onSubmit}
            disabled={isSubmitting}
        >
            {trans().ads.list.filters.submit}
        </Button>
    )

    const content = (
        <>
            <AdCategoryFiltersSubcategoriesNav
                categoryId={categoryId}
                urlQueryParams={urlQueryData}
            />
            <AdCategoryPropertyFilters
                categoryId={categoryId}
                filters={filters}
                setFilters={setFilters}
                isSubmitting={isSubmitting}
            />
            {!services && (
                <PriceFilter
                    minPrice={filters.minPrice ?? null}
                    maxPrice={filters.maxPrice ?? null}
                    setMinPrice={value => {
                        setFilters(filters => ({
                            ...filters,
                            minPrice: value,
                        }))
                    }}
                    setMaxPrice={value => {
                        setFilters(filters => ({
                            ...filters,
                            maxPrice: value,
                        }))
                    }}
                    disabled={isSubmitting}
                />
            )}
        </>
    )

    if (forModal) {
        return (
            <form
                className="modal-content m-0"
                onSubmit={onSubmit}
            >
                <ModalHeader
                    title={trans().ads.list.filters.header}
                    onClose={onClose}
                />
                <ModalBody className="pb-0">
                    {content}
                </ModalBody>
                <ModalFooter>
                    {submitButton}
                </ModalFooter>
            </form>
        )
    }

    return (
        <Card
            tag="form"
            onSubmit={onSubmit}
            className="category-filters m-0"
        >
            <CardHeader>
                <CardTitle>
                    {trans().ads.list.filters.header}
                </CardTitle>
            </CardHeader>

            <CardBody className="pt-3 pb-0">
                {content}
            </CardBody>
            <CardFooter>
                {submitButton}
            </CardFooter>
        </Card>
    )
}

// Нормализация цены (если значение получено из URL query).
function normalizePrice(price?: string | null): number | null {
    if (!price || price.trim().length === 0) {
        return null
    }
    const value = parseInt(price.trim())
    if (isNaN(value)) {
        return null
    }
    return value
}

// Конвертация фильтров в JSON строку.
function encodeFilterValuesToJson(values?: AnyObject): string | null {
    if(!values) {
        return null
    }
    // Удаляем пустые фильтры.
    const entries = Object.entries(values).filter(
        value => (Array.isArray(value[1]) ? value[1].length > 0 : !!value[1])
    )
    if (entries.length === 0) {
        return null
    }
    return JSON.stringify(Object.fromEntries(entries))
}

function decodeFilterValuesFromJson(encoded?: string | null): AnyObject {
    if (!encoded) {
        return {}
    }
    try {
        const json = JSON.parse(decodeURIComponent(encoded))
        return (typeof json === 'object') ? json as AnyObject : {}
    } catch (_e) {
        // Игнорируем.
    }
    return {}
}
